import { Arg, Ctx, FieldResolver, Query, Resolver, Root } from 'type-graphql'
import { SelectQueryBuilder } from 'typeorm'

import { User } from '@things-factory/auth-base'
import {
  Domain,
  Filter,
  getRepository,
  ObjectRef,
  Pagination,
  Sorting,
  getQueryBuilderFromListParams
} from '@things-factory/shell'

import { Setting } from '../setting/setting'
import { PartnerSetting } from './partner-setting'
import { PartnerSettingList } from './partner-setting-type'

@Resolver(PartnerSetting)
export class PartnerSettingQuery {
  @Query(returns => PartnerSetting, { description: 'To fetch a PartnerSetting' })
  async partnerSetting(
    @Arg('name') name: string,
    @Arg('partnerDomainId') partnerDomainId: string,
    @Ctx() context: ResolverContext
  ): Promise<PartnerSetting> {
    const { domain } = context.state
    const setting: Setting = await getRepository(Setting).findOne({ where: { domain: { id: domain.id }, name } })

    const partnerSetting: PartnerSetting = await getRepository(PartnerSetting).findOne({
      where: { domain: { id: domain.id }, setting: { id: setting.id }, partnerDomain: { id: partnerDomainId } }
    })

    if (!partnerSetting) throw new Error(`${name} setting is not found`)

    return partnerSetting || (setting as PartnerSetting)
  }

  @Query(returns => PartnerSettingList, { description: 'To fetch multiple PartnerSettings' })
  async partnerSettings(
    @Arg('filters', type => [Filter]) filters: Filter[],
    @Arg('pagination', type => Pagination) pagination: Pagination,
    @Arg('sortings', type => [Sorting]) sortings: Sorting[],
    @Arg('partnerDomain', type => ObjectRef) partnerDomain: ObjectRef,
    @Ctx() context: ResolverContext
  ): Promise<PartnerSettingList> {
    const { domain } = context.state
    const qb: SelectQueryBuilder<PartnerSetting> = await getQueryBuilderFromListParams({
      repository: getRepository(PartnerSetting),
      alias: 'ps',
      params: { filters, pagination, sortings },
      domain,
      searchables: ['name', 'description'],
      filtersMap: {
        name: {
          relationColumn: 'setting',
          columnName: 'name'
        },
        description: {
          relationColumn: 'setting',
          columnName: 'description'
        }
      }
    })

    qb.leftJoinAndSelect('ps.setting', 'Setting').andWhere('ps.partner_domain_id = :partnerDomainId', {
      partnerDomainId: partnerDomain.id
    })
    const [items, total] = await qb.getManyAndCount()

    return { items, total }
  }

  @FieldResolver(type => Domain)
  async partnerDomain(@Root() partnerSetting: PartnerSetting): Promise<Domain> {
    return await getRepository(Domain).findOneBy({ id: partnerSetting.partnerDomainId })
  }

  @FieldResolver(type => Setting)
  async setting(@Root() partnerSetting: PartnerSetting): Promise<Setting> {
    return await getRepository(Setting).findOneBy({ id: partnerSetting.settingId })
  }

  @FieldResolver(type => String)
  async name(@Root() partnerSetting: PartnerSetting): Promise<string> {
    if (partnerSetting.setting?.name) {
      return partnerSetting.setting.name
    }

    return (await getRepository(Setting).findOneBy({ id: partnerSetting.settingId }))?.name
  }

  async description(@Root() partnerSetting: PartnerSetting): Promise<string> {
    if (partnerSetting.setting?.description) {
      return partnerSetting.setting.description
    }

    return (await getRepository(Setting).findOneBy({ id: partnerSetting.settingId }))?.description
  }

  async category(@Root() partnerSetting: PartnerSetting): Promise<string> {
    if (partnerSetting.setting?.category) {
      return partnerSetting.setting.category
    }

    return (await getRepository(Setting).findOneBy({ id: partnerSetting.settingId }))?.category
  }

  @FieldResolver(type => Domain)
  async domain(@Root() partnerSetting: PartnerSetting): Promise<Domain> {
    return await getRepository(Domain).findOneBy({ id: partnerSetting.domainId })
  }

  @FieldResolver(type => User)
  async updater(@Root() partnerSetting: PartnerSetting): Promise<User> {
    return await getRepository(User).findOneBy({ id: partnerSetting.updaterId })
  }

  @FieldResolver(type => User)
  async creator(@Root() partnerSetting: PartnerSetting): Promise<User> {
    return await getRepository(User).findOneBy({ id: partnerSetting.creatorId })
  }
}
